import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useVendorForm } from "../VendorFormContext";
import { toast } from "react-toastify";

/**
 * VendorSubTabs - A validation-aware tab component for vendor forms
 *
 * In create mode (isCreating=true):
 * - Users can only navigate to tabs where all previous tabs are completed
 * - Clicking on a disabled tab shows a warning toast
 * - Completed tabs show a checkmark indicator
 *
 * In edit mode (isCreating=false):
 * - All tabs are accessible freely
 */
const VendorSubTabs = ({ name, link }) => {
  const navigate = useNavigate();
  const { isCreating, canAccessTab, completedTabs } = useVendorForm();

  const isAccessible = canAccessTab(link);
  const isCompleted = completedTabs.includes(link);

  const handleClick = (e) => {
    if (isCreating && !isAccessible) {
      e.preventDefault();
      toast.warning("Please complete the current tab before proceeding", {
        toastId: "tab-validation-warning",
      });
      return;
    }
  };

  // Base styles for all tabs
  const baseStyles = "p-1 flex px-4 py-2 justify-start items-center gap-2 text-xs leading-none transition-all duration-200";

  // Active tab styles
  const activeStyles = "border-b-2 border-primary-500 text-primary-500 font-bold";

  // Inactive but accessible tab styles
  const inactiveStyles = "text-text-hint font-normal hover:text-primary-400";

  // Disabled tab styles (only in create mode)
  const disabledStyles = "text-gray-400 cursor-not-allowed opacity-50";

  return (
    <NavLink
      to={link}
      onClick={handleClick}
      className={({ isActive }) => {
        if (isCreating && !isAccessible) {
          return `${baseStyles} ${disabledStyles}`;
        }
        return isActive
          ? `${baseStyles} ${activeStyles}`
          : `${baseStyles} ${inactiveStyles}`;
      }}
    >
      {/* Show checkmark for completed tabs in create mode */}
      {isCreating && isCompleted && (
        <span className="text-green-500 text-sm">✓</span>
      )}
      {name}
      {/* Show lock icon for inaccessible tabs in create mode */}
      {isCreating && !isAccessible && (
        <span className="text-gray-400 text-xs ml-1">🔒</span>
      )}
    </NavLink>
  );
};

export default VendorSubTabs;
